<template>
  <div
    :tabindex="searchable ? -1 : tabindex"
    :class="{
      'multiselect--active': isOpen,
      'multiselect--disabled': disabled,
      'multiselect--above': isAbove,
      'multiselect-clock': icon_select && value.type === 'clock',
      'direction-left': is_left,
      'is-textarea': is_textarea,
      is_placeholder_grey: is_placeholder_grey && (Array.isArray(value) ? !value.length : !value)
    }"
    class="multiselect"
    @focus="activate()"
    @blur="searchable ? false : deactivate()"
    @keydown.self.down.prevent="pointerForward()"
    @keydown.self.up.prevent="pointerBackward()"
    @keydown.enter.stop.self="addPointerElement($event)"
    @keydown.enter.tab.stop.self="addPointerElement($event)"
    @keyup.esc="deactivate()"
  >
    <slot name="caret" :toggle="toggle">
      <div
        class="multiselect__select"
        :class="[icon_select ? `multiselect__select_icon-${value.type}` : '']"
        @mousedown.prevent.stop="toggle()"
      ></div>
    </slot>

    <slot name="clear" :search="search"></slot>

    <div
      ref="tags"
      :class="[
        'multiselect__tags',
        { multiselect__tags_icon: icon_select },
        icon_select ? `multiselect__tags_icon-${value.type}` : ''
      ]"
    >
      <div v-show="visibleValues.length > 0" class="multiselect__tags-wrap">
        <template v-for="(option, index) in visibleValues" @mousedown.prevent>
          <slot name="tag" :option="option" :search="search" :remove="removeElement">
            <span :key="index" class="multiselect__tag">
              <span v-text="getOptionLabel(option)"></span>
              <i
                aria-hidden="true"
                tabindex="1"
                class="multiselect__tag-icon"
                @keydown.enter.prevent="removeElement(option)"
                @mousedown.prevent="removeElement(option)"
              ></i>
            </span>
          </slot>
        </template>
      </div>

      <template v-if="internalValue && internalValue.length > limit">
        <slot name="limit">
          <strong
            class="multiselect__strong"
            v-text="limitText(internalValue.length - limit)"
          ></strong>
        </slot>
      </template>

      <transition name="multiselect__loading">
        <slot name="loading">
          <div v-show="loading" class="multiselect__spinner"></div>
        </slot>
      </transition>

      <input
        v-if="searchable"
        :id="id"
        ref="search"
        :name="name"
        type="text"
        autocomplete="off"
        class="multiselect__input"
        :placeholder="$tr('common.selectOption')"
        :style="inputStyle"
        :value="search"
        :disabled="disabled"
        :tabindex="tabindex"
        @input="updateSearch($event.target.value)"
        @focus.prevent="activate()"
        @blur.prevent="deactivate()"
        @keyup.esc="deactivate()"
        @keydown.down.prevent="pointerForward()"
        @keydown.up.prevent="pointerBackward()"
        @keydown.tab.self="addPointerElement($event)"
        @keydown.enter.prevent.stop.self="addPointerElement($event)"
        @keydown.delete.stop="removeLastElement()"
      />

      <span
        v-if="isSingleLabelVisible"
        :class="[
          'multiselect__single',
          { multiselect__single_open: isOpen },
          icon_select ? `multiselect__single_icon-${value.type}` : '',
          time_format === 12 ? 'multiselect__single_icon-clock-12' : ''
        ]"
        @mousedown.prevent="toggle"
      >
        <slot name="singleLabel" :option="singleValue">
          <img
            v-if="icon_select && value.type === 'clock'"
            class="icon-clock-img"
            src="/static/images/elements/clock.svg"
          />
          {{ currentOptionLabel }}
        </slot>
      </span>

      <span v-if="isPlaceholderVisible" @mousedown.prevent="toggle">
        <slot name="placeholder">
          <span :class="['multiselect__single', { multiselect__single_open: isOpen }]">
            {{ $tr('common.selectOption') }}
          </span>
        </slot>
      </span>
    </div>

    <transition name="modal">
      <div
        v-show="isOpen"
        ref="list"
        class="multiselect__content-wrapper"
        :style="{ maxHeight: optimizedHeight + 'px' }"
        @focus="activate"
        @mousedown.prevent
      >
        <ul
          v-if="filteredOptions.length !== 0 || search"
          class="multiselect__content"
          :style="contentStyle"
        >
          <slot name="beforeList"></slot>
          <li v-if="multiple && max === internalValue.length">
            <span class="multiselect__option">
              <slot name="maxElements">
                {{ $tr('common.selectMaxElements', { max: max }) }}
              </slot>
            </span>
          </li>

          <template v-if="!max || internalValue.length < max">
            <li
              v-for="(option, index) in filteredOptions"
              :key="index"
              class="multiselect__element"
            >
              <span
                v-if="!(option && (option.$isLabel || option.$isDisabled))"
                :class="[
                  'multiselect__option',
                  optionHighlight(index, option),
                  icon_select ? `multiselect__option--icon-${option.type}` : ''
                ]"
                :data-select="option && option.isTag ? tagPlaceholder : selectLabelText"
                :data-selected="selectedLabelText"
                :data-deselect="deselectLabelText"
                @click.stop="select(option)"
                @mouseenter.self="pointerSet(index)"
              >
                <slot name="option" :option="option" :search="search">
                  <span>{{ getOptionLabel(option) }}</span>
                </slot>
                <span v-if="option.subname" class="subname">{{ option.subname }}</span>
              </span>

              <span
                v-if="option && (option.$isLabel || option.$isDisabled)"
                :data-select="groupSelect && selectGroupLabelText"
                :data-deselect="groupSelect && deselectGroupLabelText"
                :class="['multiselect__option', groupHighlight(index, option)]"
                @mouseenter.self="groupSelect && pointerSet(index)"
                @mousedown.prevent="selectGroup(option)"
              >
                <slot name="option" :option="option" :search="search">
                  <span>{{ getOptionLabel(option) }}</span>
                </slot>
              </span>
            </li>
          </template>

          <li v-show="showNoResults && filteredOptions.length === 0 && search && !loading">
            <span class="multiselect__option">
              <slot name="noResult">{{ $tr('common.selectNoResult') }}</slot>
            </span>
          </li>
          <slot name="afterList"></slot>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script>
import mixin from './mixin';
import pointer from './pointer';

export default {
  name: 'VueMultiselect',

  mixins: [mixin, pointer],

  props: {
    /**
     * name attribute to match optional label element
     * @default ''
     * @type {String}
     */
    name: {
      type: String,
      default: ''
    },

    /**
     * String to show when pointing to an option
     * @default 'Press enter to select'
     * @type {String}
     */
    selectLabel: {
      type: String,
      default: 'Press enter to select'
    },

    /**
     * String to show when pointing to an option
     * @default 'Press enter to select'
     * @type {String}
     */
    selectGroupLabel: {
      type: String,
      default: 'Press enter to select group'
    },

    /**
     * String to show next to selected option
     * @default 'Selected'
     * @type {String}
     */
    selectedLabel: {
      type: String,
      default: 'Selected'
    },

    /**
     * String to show when pointing to an alredy selected option
     * @default 'Press enter to remove'
     * @type {String}
     */
    deselectLabel: {
      type: String,
      default: 'Press enter to remove'
    },

    /**
     * String to show when pointing to an alredy selected option
     * @default 'Press enter to remove'
     * @type {String}
     */
    deselectGroupLabel: {
      type: String,
      default: 'Press enter to deselect group'
    },

    /**
     * Decide whether to show pointer labels
     * @default true
     * @type {Boolean}
     */
    showLabels: {
      type: Boolean,
      default: true
    },

    /**
     * Limit the display of selected options. The rest will be hidden within the limitText string.
     * @default 99999
     * @type {Integer}
     */
    limit: {
      type: Number,
      default: 99999
    },

    /**
     * Sets maxHeight style value of the dropdown
     * @default 300
     * @type {Integer}
     */
    maxHeight: {
      type: Number,
      default: 300
    },

    /**
     * Function that process the message shown when selected
     * elements pass the defined limit.
     * @default 'and * more'
     * @param {Int} count Number of elements more than limit
     * @type {Function}
     */
    limitText: {
      type: Function,
      default: count => `and ${count} more`
    },

    /**
     * Set true to trigger the loading spinner.
     * @default False
     * @type {Boolean}
     */
    loading: {
      type: Boolean,
      default: false
    },

    /**
     * Disables the multiselect if true.
     * @default false
     * @type {Boolean}
     */
    disabled: {
      type: Boolean,
      default: false
    },

    /**
     * Fixed opening direction
     * @default ''
     * @type {String}
     */
    openDirection: {
      type: String,
      default: ''
    },

    showNoResults: {
      type: Boolean,
      default: true
    },

    tabindex: {
      type: Number,
      default: 0
    },

    icon_select: {
      type: Boolean,
      default: false
    },

    time_format: {
      type: Number,
      default: 0
    },

    is_left: {
      type: Boolean,
      default: false
    },

    is_textarea: {
      type: Boolean,
      default: false
    },

    allowEmpty: {
      type: Boolean,
      default: true
    },

    is_placeholder_grey: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    isSingleLabelVisible() {
      return this.singleValue && (!this.isOpen || !this.searchable) && !this.visibleValues.length;
    },

    isPlaceholderVisible() {
      return !this.internalValue.length && (!this.searchable || !this.isOpen);
    },

    visibleValues() {
      return this.multiple ? this.internalValue.slice(0, this.limit) : [];
    },

    singleValue() {
      return this.internalValue[0];
    },

    deselectLabelText() {
      return this.showLabels ? this.deselectLabel : '';
    },

    deselectGroupLabelText() {
      return this.showLabels ? this.deselectGroupLabel : '';
    },

    selectLabelText() {
      return this.showLabels ? this.selectLabel : '';
    },

    selectGroupLabelText() {
      return this.showLabels ? this.selectGroupLabel : '';
    },

    selectedLabelText() {
      return this.showLabels ? this.selectedLabel : '';
    },

    inputStyle() {
      if (this.searchable || (this.multiple && this.value && this.value.length)) {
        // Hide input by setting the width to 0 allowing it to receive focus
        return this.isOpen ? { width: 'auto' } : { width: '0', position: 'absolute', padding: '0' };
      }

      return { width: 'auto' };
    },

    contentStyle() {
      return this.options.length ? { display: 'inline-block' } : { display: 'block' };
    },

    isAbove() {
      if (this.openDirection === 'above' || this.openDirection === 'top') {
        return true;
      } else if (this.openDirection === 'below' || this.openDirection === 'bottom') {
        return false;
      } else {
        return this.prefferedOpenDirection === 'above';
      }
    },

    showSearchInput() {
      return (
        this.searchable &&
        (this.hasSingleSelectedSlot && (this.visibleSingleValue || this.visibleSingleValue === 0) ? this.isOpen : true)
      );
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/fonts.scss';

fieldset[disabled] .multiselect {
  pointer-events: none;
}

.multiselect {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  width: 100%;
  max-width: 200px;
  min-height: 26px;
  text-align: left;
  color: $main-dark;
  border-radius: 2px;
  background-color: $white;
  border: 1px solid $grey-light;
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  cursor: pointer;
  transition: border 0.3s;

  &.full_width {
    max-width: 100%;
    width: 100%;
  }

  &:focus {
    outline: none;
    border: 1px solid $grey-light;
    transition: border 0.3s;
  }

  &.multiselect--disabled {
    opacity: 0.6;
  }

  &.error {
    border-color: $red;
  }
}

.multiselect__spinner {
  position: absolute;
  right: 1px;
  width: 40px;
  height: 24px;
  display: block;
  background: $white;

  &:after,
  &:before {
    content: '';
    position: absolute;
    width: 14px;
    height: 14px;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    border-radius: 100%;
    border-color: $green transparent transparent;
    border-style: solid;
    border-width: 2px;
  }

  &:after {
    animation: a 2.4s cubic-bezier(0.51, 0.09, 0.21, 0.8);
    animation-iteration-count: infinite;
  }

  &:before {
    animation: a 2.4s cubic-bezier(0.41, 0.26, 0.2, 0.62);
    animation-iteration-count: infinite;
  }
}

.multiselect__loading-enter-active,
.multiselect__loading-leave-active {
  transition: opacity 0.4s ease-in-out;
  opacity: 1;
}

.multiselect__loading-enter,
.multiselect__loading-leave-active {
  opacity: 0;
}

.multiselect,
.multiselect__input,
.multiselect__single {
  touch-action: manipulation;
  @include mixin-text-main;

  input {
    border: 0;
    background: transparent;
    padding: 0;
  }
}

.multiselect__select_icon-clock {
  display: none;
}

.multiselect__tags.multiselect__tags_icon-clock {
  width: 100%;
  padding: 9px 0;
  height: 38px;
}

.multiselect__choice,
.multiselect__single {
  &_open,
  &_value {
    color: #9faab6;
  }
  &_icon-choice,
  &_icon-single {
    padding-left: 30px;

    &:before {
      content: '';
      position: absolute;
      left: 2px;
      top: 50%;
      display: block;
      transform: translateY(-50%);
      width: 19px;
      height: 19px;
      background: url('/static/images/elements/types-quest/single.svg') no-repeat;
    }
  }
  &_icon-multichoice,
  &_icon-multiple {
    padding-left: 30px;
    &:before {
      content: '';
      position: absolute;
      left: -9px;
      top: -11px;
      display: block;
      width: 40px;
      height: 40px;
      background: url('/static/images/elements/types-quest/multiple.svg') no-repeat;
    }
  }
  &_icon-value,
  &_icon-open {
    padding-left: 30px;
    &:before {
      content: '';
      position: absolute;
      left: 2px;
      top: 50%;
      display: block;
      transform: translateY(-50%);
      width: 20px;
      height: 18px;
      background: url('/static/images/elements/types-quest/open.svg') no-repeat;
    }
  }
  &_icon-date {
    padding-left: 30px;

    &:before {
      content: '';
      position: absolute;
      left: 3px;
      top: 50%;
      display: block;
      transform: translateY(-50%);
      width: 17px;
      height: 19px;
      background: url('/static/images/elements/types-quest/date.svg') no-repeat;
    }
  }
  &_icon-file {
    padding-left: 30px;

    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      display: block;
      transform: translateY(-50%);
      width: 21px;
      height: 14px;
      background: url('/static/images/elements/types-quest/file.svg') no-repeat;
    }
  }
  &_icon-enumeration {
    padding-left: 30px;

    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      display: block;
      transform: translateY(-50%) translateX(-3px);
      width: 33px;
      height: 33px;
      background: url('/static/images/elements/types-quest/enumeration.svg') no-repeat;
    }
  }
  &_icon-clock {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.icon-clock-img {
  width: 21px;
  height: 21px;
  margin-right: 9px;
}

.multiselect--active {
  z-index: 11;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border: 1px solid $grey-darker;
}

.multiselect__input,
.multiselect__choice,
.multiselect__single {
  position: relative;
  min-height: 18px;
  width: 100%;
  transition: border 0.1s ease;
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.multiselect-clock {
  .multiselect__input {
    text-align: center;
    padding: 0 30px;
  }
}

.multiselect__tag ~ {
  .multiselect__input,
  .multiselect__choice,
  .multiselect__single {
    width: auto;
  }
}

.multiselect__input:focus {
  border-color: #a8a8a8;
  outline: none;
}

.multiselect__tags {
  min-height: 26px;
  order: 1;
  display: flex;
  align-items: center;
  padding: 10px 20px 10px 10px;
  box-sizing: border-box;
  min-width: calc(100% - 40px);
  flex-wrap: wrap;
  box-sizing: border-box;

  &_icon {
    padding: 9px 0 9px 8px;
    min-width: calc(100% - 30px);
  }
}

.select-tags {
  max-width: 100%;

  .multiselect__tags {
    padding: 9px 13px;
    min-height: 23px;

    .multiselect__tag {
      font-size: 13px;
      padding-right: 20px;
    }

    .multiselect__tag-icon {
      &:focus,
      &:hover {
        background: none;
      }

      &:after {
        color: white;
      }
    }
  }

  .multiselect__input,
  .multiselect__single,
  .multiselect__choice {
    min-height: 18px;
    height: 18px;
    line-height: 18px;
  }
}

.is-textarea {
  &.select-tags {
    .multiselect__tags {
      min-height: 60px;
      align-items: flex-start;

      .multiselect__tags-wrap {
        min-width: auto;
        display: inline-block;
        vertical-align: middle;
      }

      .multiselect__input {
        position: static !important;
        display: inline-block;
      }
    }

    .multiselect__select {
      display: none;
    }
  }
}

.is_placeholder_grey {
  &.multiselect {
    .multiselect__choice,
    .multiselect__single {
      color: #9faab6;
    }
  }
}

.multiselect__content-wrapper {
  position: absolute;
  width: calc(100% + 4px);
  top: 100%;
  left: -1px;
  transition: all ease-in-out 0.4s;
  z-index: 9;
  box-shadow: $box-shadow;
  border: 1px solid $grey-light;
}

.multiselect__tags-wrap {
  min-width: 100%;

  .multiselect__tag {
    box-sizing: border-box;
    overflow: hidden;
    max-width: 100%;
    text-overflow: ellipsis;
    border-radius: 2px;
    margin-top: 1px;
    margin-bottom: 1px;
  }
}
.custom-multiselect {
  .multiselect__input {
    padding: 2px 8px;
  }

  .multiselect__tags {
    padding: 8px;

    .multiselect__tags-wrap {
      min-width: initial;

      .multiselect__tag {
        margin-top: 2px;
        margin-bottom: 2px;
      }
    }
  }
}

.multiselect__tag {
  position: relative;
  display: inline-block;
  padding: 4px 26px 4px 10px;
  border-radius: 2px;
  margin-right: 10px;
  color: #fff;
  line-height: 1;
  background: $green;
  margin-bottom: 8px;
  white-space: nowrap;
}

.multiselect__tag-icon {
  cursor: pointer;
  margin-left: 7px;
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  font-weight: 700;
  font-style: normal;
  width: 22px;
  text-align: center;
  line-height: 22px;
  transition: all 0.2s ease;
  border-radius: 2px;

  &:after {
    content: '\D7';
    color: #266d4d;
    font-size: 14px;
  }

  &:focus,
  &:hover {
    background: #369a6e;
  }
  &:focus:after,
  &:hover:after {
    color: #fff;
  }
}

.multiselect__current {
  min-height: 40px;
  overflow: hidden;
  padding: 8px 12px 0;
  padding-right: 30px;
  white-space: nowrap;
  border-radius: 2px;
  border: 1px solid #e8e8e8;
  line-height: 16px;
  box-sizing: border-box;
  display: block;
  margin: 0;
  text-decoration: none;
  cursor: pointer;
}

.multiselect__select {
  cursor: pointer;
  width: 40px;
  height: 40px;
  padding: 0;
  text-align: center;
  order: 2;
  margin-left: -20px;
  box-sizing: border-box;
}

.multiselect__select:after {
  font-size: 12px;
  font-family: FontAwesome;
  content: '';
  width: 11px;
  height: 6px;
  top: 50%;
  right: 10px;
  margin-top: -3px;
  position: absolute;
  background: url('/static/icons/drop_arrow.svg') center no-repeat;
  transform: rotate(-180deg);
  transition: all ease-in-out 0.4s;
}

.multiselect--active {
  .multiselect__select:after {
    transform: rotate(0deg);
  }
}

.multiselect__placeholder {
  color: #adadad;
  display: inline-block;
  margin-bottom: 10px;
  padding-top: 2px;
}

.multiselect--active .multiselect__placeholder {
  display: none;
}

.multiselect--above {
  .multiselect__content {
    bottom: 100%;
    border-bottom: none;
    border-top: 1px solid $grey-light;
  }

  .multiselect__content-wrapper {
    top: 0;
  }
}

.multiselect__content {
  position: absolute;
  list-style: none;
  display: block;
  width: 100%;
  max-height: 240px;
  margin: 0;
  padding: 0;
  overflow: auto;
  border: 1px solid $grey-light;
  left: -1px;
  margin-top: -1px;
  border-radius: 2px;
  background: $white-color;
  z-index: 1;

  &::webkit-scrollbar {
    display: none;
  }
}

.multiselect {
  &.big {
    width: 200px;
    .multiselect__tags {
      min-width: calc(100% - 7px);
      min-height: 38px;
    }
    .multiselect__content-wrapper {
      top: calc(100% + 10px);
    }

    .multiselect__content {
      width: 350px;
    }

    &.multiselect--above {
      .multiselect__content-wrapper {
        top: -5px;
      }

      .multiselect__content {
        border-bottom: 1px solid $grey-light;
      }
    }
  }

  &.bottom {
    .multiselect__content-wrapper {
      top: 100%;
    }
  }

  &.w {
    .multiselect__content-wrapper {
      top: calc(100% + 7px);
    }
    .multiselect__option--selected {
      padding-right: 30px;

      &:after {
        display: none;
      }
    }
  }

  &.direction-left {
    .multiselect__content {
      left: auto;
      right: -1px;
    }
  }
}

.multiselect__element {
  display: block;
}

.subname {
  display: block;
  color: #9faab6;
  font-size: 12px;
  line-height: 18px;
}

.multiselect__option {
  display: block;
  padding: 10px 20px 10px 20px;
  text-decoration: none;
  text-transform: none;
  // vertical-align: middle;
  position: relative;
  cursor: pointer;
  text-overflow: ellipsis;
  overflow: hidden;

  &:after {
    top: 0;
    right: 0;
    position: absolute;
    line-height: 40px;
    padding-right: 12px;
    padding-left: 20px;
  }

  &--icon-clock {
    text-align: center;
  }
}

.multiselect__option--highlight {
  background: $line-statuses-status-drop-bg;
  outline: none;

  &:active {
    background: $line-statuses-status-down-bg;
  }
}

.multiselect__option--selected {
  padding-right: 30px;

  &:after {
    color: $text-dark-color;
    content: '\f00c';
    font-family: FontAwesome;
  }
}

.multiselect__option--icon-choice,
.multiselect__option--icon-single {
  padding-left: 40px;
  &:before {
    content: '';
    position: absolute;
    left: 11px;
    top: 50%;
    display: block;
    transform: translateY(-50%);
    width: 19px;
    height: 19px;
    background: url('/static/images/elements/types-quest/single.svg') no-repeat;
  }
}

.multiselect__option--icon-multichoice,
.multiselect__option--icon-multiple {
  padding-left: 40px;
  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    display: block;
    width: 40px;
    height: 40px;
    background: url('/static/images/elements/types-quest/multiple.svg') no-repeat;
  }
}

.multiselect__option--icon-value,
.multiselect__option--icon-open {
  padding-left: 40px;
  &:before {
    content: '';
    position: absolute;
    left: 10px;
    top: 50%;
    display: block;
    transform: translateY(-50%);
    width: 20px;
    height: 18px;
    background: url('/static/images/elements/types-quest/open.svg') no-repeat;
  }
}

.multiselect__option--icon-date {
  padding-left: 40px;
  &:before {
    content: '';
    position: absolute;
    left: 11px;
    top: 50%;
    display: block;
    transform: translateY(-50%);
    width: 17px;
    height: 19px;
    background: url('/static/images/elements/types-quest/date.svg') no-repeat;
  }
}

.multiselect__option--icon-file {
  padding-left: 40px;
  &:before {
    content: '';
    position: absolute;
    left: 11px;
    top: 50%;
    display: block;
    transform: translateY(-50%);
    width: 21px;
    height: 14px;
    background: url('/static/images/elements/types-quest/file.svg') no-repeat;
  }
}

.multiselect__option--icon-enumeration {
  padding-left: 40px;
  &:before {
    content: '';
    position: absolute;
    left: 11px;
    top: 50%;
    display: block;
    transform: translateY(-50%) translateX(-8px);
    width: 33px;
    height: 33px;
    background: url('/static/images/elements/types-quest/enumeration.svg') no-repeat;
  }
}

.multiselect--disabled {
  pointer-events: none;

  .multiselect__current {
    color: #a6a6a6;
  }
}

.multiselect__option--disabled {
  background: #ededed;
  color: #a6a6a6;
  cursor: text;
  pointer-events: none;

  &.multiselect__option--highlight {
    background: #dedede !important;
  }
}

.multiselect-enter-active,
.multiselect-leave-active {
  transition: all 0.15s ease;
}

.multiselect-enter,
.multiselect-leave-active {
  opacity: 0;
}

@keyframes a {
  0% {
    transform: rotate(0);
  }

  to {
    transform: rotate(2turn);
  }
}

.multiselect.tight {
  .multiselect__tags {
    padding: 6px 4px;
  }

  .multiselect__select {
    width: 13px;
    padding: 4px 6px 4px 0;

    &:after {
      right: 5px;
    }
  }
}

.multiselect.open-reg {
  .multiselect__input,
  .multiselect__single {
    min-height: 18px;
    line-height: 18px;
  }

  .multiselect__select:after {
    top: initial;
  }
}

.multiselect.phone-masks {
  .multiselect__option {
    display: flex;
    justify-content: space-between;

    &.multiselect__option--selected {
      padding-right: 10px;
      background: #f1f2f2;

      &:after {
        display: none;
      }
    }

    .option__desc.wrap {
      white-space: normal;
      padding-right: 13px;
    }

    .option__desc.nowrap {
      white-space: nowrap;
    }
  }
}
</style>
